/**
 * /fr/prd-document-exigences-produit — PM concept page (FR, single-locale).
 *
 * Targets prd produit (40) / prd signification / documentation produit. Concept
 * + the PRD-drift reframe. FR has the cleanest genuine PM seam (Slite indexed).
 */
import { MarketingShell } from './MarketingShell';

export function FrPrd() {
  return (
    <MarketingShell
      currentLocale="fr"
      hreflangPath="/prd-document-exigences-produit"
      hreflangLocales={['fr']}
      documentTitle="PRD : le document d'exigences produit (modèle + exemple) · Draftlize"
      documentDescription="Qu'est-ce qu'un PRD ? Définition, structure et modèle du document d'exigences produit — et comment le garder vivant quand les décisions changent, avec Draftlize."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">Guide · PRD</span>
          <h1 className="serif" style={{ fontSize: 'clamp(36px, 4.8vw, 58px)', lineHeight: 1.05, margin: '12px 0 24px' }}>
            Le PRD (document<br />
            <em>d'exigences produit), expliqué.</em>
          </h1>
          <p className="lede">
            Le <strong>PRD</strong> (<em>Product Requirements Document</em>, ou document d'exigences produit) décrit <em>ce qu'il faut construire</em> et <em>pourquoi</em> : le problème, le périmètre, les exigences et les décisions qui les sous-tendent, pour que l'équipe et les parties prenantes partagent la même vision. Le vrai problème arrive après : au bout de deux ou trois sprints, le PRD ne correspond plus à la réalité — il dérive. Voici sa structure, un modèle, et comment éviter cette dérive.
          </p>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">La structure d'un PRD qui tient la route.</h2>
        </div>
        <div style={{ maxWidth: 760 }}>
          <ol style={{ fontSize: 16, lineHeight: 2, color: 'var(--ink-1)', margin: 0, paddingLeft: 22 }}>
            <li><strong>Problème & objectifs</strong> — le problème de qui, pourquoi maintenant, quel résultat visé.</li>
            <li><strong>Périmètre (in/out)</strong> — ce qui est délibérément hors champ.</li>
            <li><strong>Exigences</strong> — fonctionnelles et non fonctionnelles, priorisées.</li>
            <li><strong>Décisions & arbitrages</strong> — ce qui a été pesé puis écarté.</li>
            <li><strong>Questions ouvertes & critères de succès</strong>.</li>
          </ol>
          <p style={{ fontSize: 16, lineHeight: 1.8, color: 'var(--ink-1)', margin: '20px 0 0' }}>
            Ce modèle se copie tel quel. La partie la plus précieuse — et la plus vite périmée — est le point 4 : les décisions. Un PRD n'est que la pointe émergée des entretiens de découverte et des décisions qui l'ont façonné.
          </p>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">Le vrai problème : <em>le PRD dérive après chaque sprint.</em></h2>
          <p style={{ maxWidth: 720, marginTop: 12, color: 'var(--ink-1)', fontSize: 16 }}>
            Un PRD est juste le jour de sa validation. Puis une réunion change une hypothèse, un cas limite renverse une décision — et le document, lui, ne bouge pas. Personne ne maintient à la main un document de douze pages. Des semaines plus tard, un agent IA construit sur une spec qui n'est plus vraie.
          </p>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">Les exigences deviennent des cartes</h3>
            <p>Chaque exigence et chaque décision devient une carte adressable, plutôt qu'une ligne en page sept — avec sa justification et son responsable, trouvable par ID.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">Une décision change, le PRD le signale</h3>
            <p>Quand une décision plus récente en renverse une ancienne, les cartes dépendantes — et la partie concernée du PRD — passent automatiquement en « stale ». Le document ne ment plus en silence pendant trois semaines.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Votre agent IA lit la spec à jour</h3>
            <p>Claude Code ou Cursor lisent le PRD via MCP — un contexte à jour ou visiblement marqué, et non un document figé dont personne ne connaît l'état.</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          Un PRD est juste le jour de sa validation. Le problème, ce sont les six semaines suivantes, où personne ne le met à jour.
        </blockquote>
        <cite>Faites des exigences des cartes — le PRD signale alors lui-même quand il devient obsolète.</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">FAQ</span>
            <h2 className="serif">Questions fréquentes.</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820 }}>
          {[
            { q: 'PRD ou cahier des charges, quelle différence ?', a: 'Proches, mais pas identiques. Le cahier des charges classique est souvent figé et contractuel ; un PRD est plus léger, orienté résultat et vit avec le produit. C’est justement pourquoi il doit rester à jour.' },
            { q: 'Qui rédige le PRD ?', a: 'En général le ou la product manager, avec l’ingénierie et le design. L’essentiel est que les décisions à l’intérieur restent traçables et justifiées.' },
            { q: 'Quelle longueur pour un PRD ?', a: 'Aussi court que possible tout en couvrant problème, périmètre, exigences et décisions. La longueur n’est pas le but ; la clarté et la fraîcheur le sont.' },
            { q: 'Comment éviter qu’il devienne obsolète ?', a: 'En faisant des exigences et des décisions des cartes liées plutôt que des paragraphes figés. Quand une décision change, Draftlize marque automatiquement les endroits dépendants comme « stale ».' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.8, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
        <p style={{ maxWidth: 820, margin: '8px auto 0', fontSize: 15, color: 'var(--ink-1)' }}>
          À lire ensuite : <a href="/fr/retranscription-entretien" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>retranscrire un entretien utilisateur</a> et en tirer des décisions.
        </p>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">Un PRD qui ne dérive plus <em>— gratuit avec 5 $.</em></h2>
        </div>
        <div className="trust-strip">
          <span>5 $ offerts à l'inscription</span>
          <span>Payez seulement ce que vous utilisez</span>
          <span>Le solde n'expire jamais</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            Écrivez votre PRD sous forme de cartes liées. Chaque exigence devient adressable, et une décision modifiée marque automatiquement les endroits concernés — fini la dérive du PRD.
          </p>
          <a className="btn-primary" href="/">Commencer gratuitement avec 5 $</a>
        </div>
      </section>
    </MarketingShell>
  );
}
